import * as React from "react";
import { useRef, useEffect, useCallback } from "react";
import { Button, Caption1, makeStyles, tokens } from "@fluentui/react-components";
import { Add24Regular, ArrowDown24Regular, Delete24Regular } from "@fluentui/react-icons";
import DOMPurify from "dompurify";

// ─── Styles ───────────────────────────────────────────────────────────────────

const useStyles = makeStyles({
  root: {
    display: "flex",
    flexDirection: "column",
    gap: "6px",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
    boxSizing: "border-box",
  },
  toolbar: {
    display: "flex",
    alignItems: "center",
    gap: "6px",
    padding: "6px 8px",
    backgroundColor: tokens.colorNeutralBackground3,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderBottom: "none",
    borderTopLeftRadius: tokens.borderRadiusMedium,
    borderTopRightRadius: tokens.borderRadiusMedium,
    flexWrap: "wrap",
  },
  toolbarDivider: {
    width: "1px",
    height: "16px",
    backgroundColor: tokens.colorNeutralStroke2,
    flexShrink: 0,
  },
  editorScroll: {
    minHeight: "220px",
    maxHeight: "320px",
    overflowY: "auto",
    overflowX: "auto",
    border: `1px solid ${tokens.colorNeutralStroke1}`,
    borderBottomLeftRadius: tokens.borderRadiusMedium,
    borderBottomRightRadius: tokens.borderRadiusMedium,
    backgroundColor: "#ffffff",
    padding: "10px",
    boxSizing: "border-box",
    width: "100%",
    minWidth: 0,
    maxWidth: "100%",
  },
});

// ─── Shared cell styles injected at runtime ───────────────────────────────────

const HEADER_STYLE =
  "background-color:#f3f2f1;padding:10px 14px;border:0.5pt solid #d1d1d1;text-align:left;font-weight:700;font-family:Calibri,sans-serif;font-size:10pt;";
const CELL_STYLE =
  "padding:10px 14px;border:0.5pt solid #d1d1d1;vertical-align:top;color:#444;font-family:Calibri,sans-serif;font-size:10pt;min-width:80px;";

// ─── Global editor CSS injected once ─────────────────────────────────────────

const EDITOR_CSS = `
  .mp-template-editor table {
    border-collapse: collapse;
    width: 100%;
    table-layout: auto;
  }
  .mp-template-editor th {
    background-color: #f3f2f1;
    padding: 10px 14px;
    border: 0.5pt solid #d1d1d1;
    text-align: left;
    font-weight: 700;
    font-family: Calibri, sans-serif;
    font-size: 10pt;
    min-width: 80px;
  }
  .mp-template-editor td {
    padding: 10px 14px;
    border: 0.5pt solid #d1d1d1;
    vertical-align: top;
    color: #444;
    font-family: Calibri, sans-serif;
    font-size: 10pt;
    min-width: 80px;
  }
  .mp-template-editor td:focus,
  .mp-template-editor th:focus {
    outline: 2px solid #59766C;
    outline-offset: -2px;
  }
  .mp-template-editor p {
    margin: 4px 0;
    font-family: Calibri, sans-serif;
    font-size: 10pt;
  }
  .mp-template-editor strong {
    font-weight: 700;
  }
`;

function injectEditorCss() {
  if (document.getElementById("mp-template-editor-css")) return;
  const style = document.createElement("style");
  style.id = "mp-template-editor-css";
  style.textContent = EDITOR_CSS;
  document.head.appendChild(style);
}

// ─── TemplateEditor Component ─────────────────────────────────────────────────

interface TemplateEditorProps {
  /** The initial HTML (table markup) to render */
  initialHtml: string;
  /** Called whenever the user changes content */
  onChange: (html: string) => void;
}

const TemplateEditor: React.FC<TemplateEditorProps> = ({ initialHtml, onChange }) => {
  const styles = useStyles();
  const editorRef = useRef<HTMLDivElement>(null);
  const isInitialized = useRef(false);

  // Inject CSS once
  useEffect(() => {
    injectEditorCss();
  }, []);

  // Set initial content once on mount.
  // DOMPurify sanitizes the HTML and returns a DocumentFragment (no innerHTML
  // assignment) so SAST scanners see no unsafe DOM write path.
  useEffect(() => {
    if (editorRef.current && !isInitialized.current) {
      const fragment = DOMPurify.sanitize(initialHtml, {
        ALLOWED_TAGS: [
          "table", "thead", "tbody", "tr", "th", "td",
          "p", "strong", "em", "b", "i", "u", "br",
          "ul", "ol", "li", "span", "div",
          "h1", "h2", "h3", "h4", "h5", "h6",
          "colgroup", "col",
        ],
        ALLOWED_ATTR: ["style", "class", "colspan", "rowspan", "contenteditable"],
        RETURN_DOM_FRAGMENT: true,
      }) as DocumentFragment;
      editorRef.current.replaceChildren(fragment);
      isInitialized.current = true;
    }
  }, [initialHtml]);

  const emitChange = useCallback(() => {
    if (editorRef.current) {
      onChange(editorRef.current.innerHTML);
    }
  }, [onChange]);

  // Tab key → move to next cell (like a spreadsheet)
  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (e.key !== "Tab") return;
    e.preventDefault();

    const sel = window.getSelection();
    if (!sel || !sel.anchorNode) return;

    // Walk up to find the current cell (td or th)
    let cell: HTMLElement | null = sel.anchorNode as HTMLElement;
    while (cell && cell !== editorRef.current) {
      if (cell.tagName === "TD" || cell.tagName === "TH") break;
      cell = cell.parentElement;
    }
    if (!cell || cell === editorRef.current) return;

    const allCells = Array.from(
      editorRef.current?.querySelectorAll("td, th") || []
    ) as HTMLElement[];
    const idx = allCells.indexOf(cell);

    if (e.shiftKey) {
      // Shift+Tab → previous cell
      if (idx > 0) focusCell(allCells[idx - 1]);
    } else {
      if (idx < allCells.length - 1) {
        focusCell(allCells[idx + 1]);
      } else {
        // At last cell → add a new row
        addRow();
      }
    }
  };

  // ─── Table Manipulation ──────────────────────────────────────────────────

  const getTable = (): HTMLTableElement | null => {
    return editorRef.current?.querySelector("table") || null;
  };

  // Move the caret into a cell by placing a selection range. Works even when
  // cells aren't individually focusable (e.g. imported tables without a
  // per-cell contenteditable attribute), where element.focus() is a no-op.
  const focusCell = (cell: HTMLElement | null | undefined) => {
    if (!cell) return;
    const range = document.createRange();
    range.selectNodeContents(cell);
    const sel = window.getSelection();
    sel?.removeAllRanges();
    sel?.addRange(range);
    cell.scrollIntoView({ block: "nearest", inline: "nearest" });
  };

  const addRow = useCallback(() => {
    const table = getTable();
    if (!table) return;

    const rows = table.querySelectorAll("tr");
    if (rows.length === 0) return;

    // Count cells from the last body row (not header)
    const lastRow = rows[rows.length - 1];
    const colCount = lastRow.cells.length;

    const newRow = table.insertRow(-1);
    for (let i = 0; i < colCount; i++) {
      const cell = newRow.insertCell(i);
      cell.setAttribute("style", CELL_STYLE);
      cell.contentEditable = "true";
      cell.innerHTML = "";
    }

    emitChange();

    // Focus first cell of new row
    focusCell(newRow.cells[0] as HTMLElement);
  }, [emitChange]);

  const addColumn = useCallback(() => {
    const table = getTable();
    if (!table) return;

    const rows = table.querySelectorAll("tr");
    rows.forEach((row, i) => {
      if (i === 0) {
        // Header row → add a <th>
        const th = document.createElement("th");
        th.setAttribute("style", HEADER_STYLE);
        th.contentEditable = "true";
        th.textContent = "Column";
        row.appendChild(th);
      } else {
        // Body rows → add a <td>
        const td = document.createElement("td");
        td.setAttribute("style", CELL_STYLE);
        td.contentEditable = "true";
        td.innerHTML = "";
        row.appendChild(td);
      }
    });

    emitChange();

    // Focus the new header cell
    const table2 = getTable();
    const headerRow = table2?.querySelector("tr");
    if (headerRow) {
      const cells = headerRow.querySelectorAll("th");
      focusCell(cells[cells.length - 1] as HTMLElement);
    }
  }, [emitChange]);

  const deleteLastRow = useCallback(() => {
    const table = getTable();
    if (!table) return;
    const rows = table.querySelectorAll("tr");
    // Don't delete the header row
    if (rows.length <= 2) return;
    rows[rows.length - 1].remove();
    emitChange();
  }, [emitChange]);

  return (
    <div className={styles.root}>
      {/* ── Toolbar ── */}
      <div className={styles.toolbar}>
        <Caption1 style={{ color: tokens.colorNeutralForeground3, marginRight: "4px", fontSize: "10px", fontWeight: 600 }}>
          TABLE
        </Caption1>
        <div className={styles.toolbarDivider} />
        <Button
          size="small"
          appearance="subtle"
          icon={<ArrowDown24Regular style={{ width: 16, height: 16 }} />}
          onClick={addRow}
          title="Add row below"
        >
          + Row
        </Button>
        <Button
          size="small"
          appearance="subtle"
          icon={<Add24Regular style={{ width: 16, height: 16 }} />}
          onClick={addColumn}
          title="Add column to the right"
        >
          + Column
        </Button>
        <div className={styles.toolbarDivider} />
        <Button
          size="small"
          appearance="subtle"
          icon={<Delete24Regular style={{ width: 14, height: 14 }} />}
          onClick={deleteLastRow}
          title="Delete last row"
          style={{ color: tokens.colorPaletteRedForeground1 }}
        >
          Del Row
        </Button>
      </div>

      {/* ── Editable Table Area ── */}
      <div className={styles.editorScroll}>
        <div
          ref={editorRef}
          className="mp-template-editor"
          contentEditable
          suppressContentEditableWarning
          onInput={emitChange}
          onKeyDown={handleKeyDown}
          style={{ outline: "none", minHeight: "180px" }}
          spellCheck={false}
        />
      </div>

      <Caption1 style={{ color: tokens.colorNeutralForeground3, fontSize: "10px" }}>
        💡 Click any cell to edit. Press <strong>Tab</strong> to move between cells. Tab on the last cell adds a new row.
      </Caption1>
    </div>
  );
};

export default TemplateEditor;
